import { forwardRef } from 'react';
import { css } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { theme } from '@frontend/theme';
import { Button, ButtonProps, SpinningLoader, useTooltip } from '@frontend/design-system';

const styles = {
  button: css`
    text-transform: none;
    background: ${theme.colors.white};
    min-width: ${theme.spacing(20)};
    width: max(calc(34% - ${theme.spacing(2)}), ${theme.spacing(20)});

    position: relative;

    span {
      flex-direction: column;
    }
  `,
  buttonExpanded: css`
    min-height: 128px;
    padding: ${theme.spacing(2)};

    & > span {
      gap: ${theme.spacing(1)};
    }
  `,
  chip: css`
    position: absolute;
    top: 0;
    right: ${theme.spacing(1)};
    transform: translateY(-50%);
    line-height: 1;
  `,
};

export type PaymentMethodButtonProps = Omit<ButtonProps, 'variant'> & {
  chip?: React.ReactNode;
  expanded?: boolean;
  tooltip?: string;
};
export const PaymentMethodButton = forwardRef(
  ({ onClick, children, chip, expanded, loading, tooltip, ...rest }: PaymentMethodButtonProps, ref) => {
    const { triggerProps, tooltipProps, Tooltip } = useTooltip();

    const button = (
      <Button
        variant='secondary'
        css={[styles.button, expanded && styles.buttonExpanded]}
        onClick={onClick}
        {...rest}
        {...(!!tooltip ? triggerProps : {})}
        ref={composeRefs(ref, triggerProps.ref) as React.Ref<HTMLButtonElement>}
      >
        {children}
        {loading && (
          <div
            css={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <SpinningLoader size='xs' css={{ opacity: 0.5 }} />
          </div>
        )}
        {chip && <div css={styles.chip}>{chip}</div>}
      </Button>
    );

    if (tooltip) {
      return (
        <>
          {button}
          <Tooltip {...tooltipProps}>{tooltip}</Tooltip>
        </>
      );
    }

    return button;
  }
);

PaymentMethodButton.displayName = 'PaymentMethodButton';
